'use client';

import { AppCard } from '@ui/components/layouts';
import { Separator } from '@ui/components/separator';
import { text } from '@modules/garden-planner/text';
import { text as gardenPlannerText } from '@modules/garden-planner/text_business_logic';
import type { GardenSummaryProps } from '@modules/garden-planner/garden_planner_views';
import { hasPlantImage } from '@modules/plants/business_logic';
import { useState } from 'react';

import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faChevronDown } from '@micks-yard/icons/solid/faChevronDown';

export function GardenSummary({
  plantGroups,
  hasPlantsInGarden,
  totalPlanted,
  freeSpots,
}: GardenSummaryProps) {
  const [open, setOpen] = useState('');

  const isOpen = open === 'summary';

  return (
    <AppCard className="overflow-hidden p-0 shadow-sm">
      <div className="w-full overflow-hidden">
        <button
          type="button"
          className="hover:bg-muted flex w-full flex-row items-center justify-between rounded-none px-4 py-3 text-left"
          aria-expanded={isOpen}
          onClick={() => setOpen(isOpen ? '' : 'summary')}>
          <div className="flex flex-1 flex-row flex-wrap items-center gap-3">
            <h2 className="text-foreground text-lg font-semibold">{text.gardenSummary}</h2>
            <div className="flex flex-row flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                {text.planted(totalPlanted)}
              </span>
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                {text.freeSpots(freeSpots)}
              </span>
            </div>
          </div>
          <FaIcon
            icon={faChevronDown}
            className="size-5 shrink-0 opacity-60 transition-transform duration-200"
            style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </button>

        {isOpen ? (
          <div className="px-4 pt-0 pb-4">
            <Separator className="mb-3" />
            <div className="flex flex-col gap-4">
              {hasPlantsInGarden
                ? plantGroups.map((group) => (
                    <div key={group.category} className="flex flex-col gap-2">
                      <p className="text-muted-foreground text-sm font-semibold">
                        {group.category}
                      </p>
                      <div className="flex flex-row flex-wrap gap-2">
                        {group.plants.map((plant) => {
                          const label = gardenPlannerText.plantDisplayName(
                            plant.name,
                            plant.speciesName
                          );
                          return (
                            <div
                              key={plant.plantId}
                              className="border-border bg-muted hover:bg-muted/80 flex flex-row items-center gap-1.5 rounded-lg border px-2 py-1.5">
                              {hasPlantImage(plant.imagePath) ? (
                                <img
                                  src={plant.imagePath}
                                  alt={label}
                                  width={20}
                                  height={20}
                                  className="rounded"
                                />
                              ) : (
                                <span className="text-base">{plant.emoji}</span>
                              )}
                              <span className="text-foreground text-sm font-medium">{label}</span>
                              <span className="flex min-w-[18px] items-center justify-center rounded-full bg-gray-500 px-1.5 py-0.5 text-xs font-bold text-white">
                                {plant.count}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                : null}

              {!hasPlantsInGarden ? (
                <p className="text-muted-foreground text-sm italic">{text.noPlantsYet}</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </AppCard>
  );
}
